<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { isRole } from '@/utils/roleHelper';
import { loadLanguageAsync, trans } from 'laravel-vue-i18n';

defineOptions({
    inheritAttrs: false
});

const page = usePage();
const { profile } = storeToRefs(useUserStore());
const { setLocale } = useUserStore();
const roles = computed(() => isRole(profile.value));

const userName = computed(() => {
    if (!profile.value) {
        return '';
    }
    const lastInitial = profile.value.last_name ? `${profile.value.last_name.charAt(0)}.` : '';
    return `${profile.value.first_name ?? ''} ${lastInitial}`.trim();
});

const navItems = computed(() => {
    const items = [
        { name: 'FoodFlow/DashboardPage', path: '/foodflow', label: trans('label.dashboard') },
        { name: 'FoodFlow/SessionsPage', path: '/foodflow/sessions', label: trans('label.sessions') },
        { name: 'FoodFlow/SuppliersPage', path: '/foodflow/suppliers', label: trans('label.suppliers_recipients') },
    ];
    if (roles.value.superAdmin() || roles.value.admin()) {
        items.push({ name: 'Users/UsersPage', path: '/users', label: trans('label.users') });
    }
    return items;
});

const isActive = (item) => page.component === item.name;

const visit = (item) => {
    mobileNavOpen.value = false;
    router.visit(item.path);
};

const menuOpen = ref(false);
const toggleMenu = () => {
    menuOpen.value = !menuOpen.value;
};

const mobileNavOpen = ref(false);
const toggleMobileNav = () => {
    mobileNavOpen.value = !mobileNavOpen.value;
};

watch(mobileNavOpen, (open) => {
    document.body.style.overflow = open ? 'hidden' : '';
});

onUnmounted(() => {
    document.body.style.overflow = '';
});

const changeLocale = (option) => {
    setLocale(option);
    loadLanguageAsync(option);
};

const logout = async () => {
    await router.post('/logout');
};
</script>

<template>
<div class="ff-shell">
    <div class="foodflow-topbar">
        <div class="flex items-center gap-3">
            <button
                type="button"
                class="foodflow-topbar-menu-btn"
                :aria-label="$t('label.menu')"
                @click="toggleMobileNav">
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"><path
                        d="M3 5h14M3 10h14M3 15h14"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round" /></svg>
            </button>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <div class="foodflow-logo">Food<span class="text-[#61B245]">Flow</span></div>
        </div>
        <div class="flex items-center gap-2.5">
            <span class="foodflow-topbar-org">{{ $t('label.organization_name') }}</span>
            <div class="foodflow-topbar-divider" />
            <div class="relative">
                <button type="button" class="text-[13px] text-white/60 hover:text-white" @click="toggleMenu">{{ userName }}</button>
                <div v-if="menuOpen" class="foodflow-user-menu" @click="menuOpen = false">
                    <button type="button" class="foodflow-user-menu-item" @click="changeLocale('en')">{{ $t('common.en') }}</button>
                    <button type="button" class="foodflow-user-menu-item" @click="changeLocale('fr')">{{ $t('common.fr') }}</button>
                    <button type="button" class="foodflow-user-menu-item" @click="logout">{{ $t('label.sign_out') }}</button>
                </div>
            </div>
        </div>
    </div>

    <div class="foodflow-tabnav">
        <nav class="foodflow-tabnav-list">
            <a
                v-for="item in navItems"
                :key="item.name"
                class="foodflow-tabnav-item font-medium"
                :class="isActive(item) ? 'border-[#61B245] font-semibold text-white' : 'border-transparent text-white/55 hover:text-white/85'"
                @click="visit(item)">
                {{ item.label }}
            </a>
        </nav>
    </div>

    <div
        v-if="mobileNavOpen"
        class="foodflow-mobile-nav-backdrop"
        @click="mobileNavOpen = false" />
    <nav
        v-if="mobileNavOpen"
        class="foodflow-mobile-nav">
        <a
            v-for="item in navItems"
            :key="item.name"
            class="foodflow-mobile-nav-item font-medium"
            :class="isActive(item) ? 'bg-white/10 font-semibold text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'"
            @click="visit(item)">
            {{ item.label }}
        </a>
    </nav>

    <div class="min-h-[calc(100vh-56px)] md:min-h-[calc(100vh-100px)]">
        <slot />
    </div>

    <div class="z-[1008]">
        <modals-container />
    </div>
</div>
</template>
